import { useEffect, useState } from "react";
import Button from "../common/Button";
import { normalizeApiError } from "../../utils/errors";
import { createCampaign } from "../../services/campaignsApi";
import { getAiConfigs } from "../../services/aiConfigApi";

const TIMEZONES = [
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Kolkata",
  "Asia/Karachi",
  "Asia/Singapore",
  "Europe/London",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "UTC",
];

// ─── Step indicator ───────────────────────────────────────────────────────────
function StepBar({ step }) {
  const steps = ["Basic Info", "Script", "Audience"];
  return (
    <div className="wizard-steps">
      {steps.map((label, i) => (
        <div key={i} className={`wizard-step${step === i + 1 ? " active" : step > i + 1 ? " done" : ""}`}>
          <div className="wizard-step-dot">{step > i + 1 ? "✓" : i + 1}</div>
          <span className="wizard-step-label">{label}</span>
          {i < steps.length - 1 && <div className="wizard-step-line" />}
        </div>
      ))}
    </div>
  );
}

// ─── Step 1: Basic Info ───────────────────────────────────────────────────────
function Step1({ form, onChange, busy }) {
  return (
    <div className="wizard-step-content">
      <div className="wizard-step-title">Campaign Details</div>
      <div className="wizard-step-subtitle">Name your campaign and set when it should run.</div>

      <label className="campaign-label">
        Campaign Name <span className="required-star">*</span>
        <input
          className="campaign-input"
          type="text"
          placeholder="e.g. Ramadan Weight Loss Offer"
          value={form.name}
          onChange={(e) => onChange("name", e.target.value)}
          disabled={busy}
          required
        />
      </label>

      <label className="campaign-label">
        Description
        <textarea
          className="campaign-input campaign-textarea"
          rows="2"
          placeholder="Optional — internal notes about this campaign"
          value={form.description}
          onChange={(e) => onChange("description", e.target.value)}
          disabled={busy}
        />
      </label>

      <div className="campaign-form-row">
        <label className="campaign-label">
          Schedule Date <span className="required-star">*</span>
          <input
            className="campaign-input"
            type="date"
            required
            value={form.scheduleDate}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => onChange("scheduleDate", e.target.value)}
            disabled={busy}
          />
        </label>
        <label className="campaign-label">
          Schedule Time <span className="required-star">*</span>
          <input
            className="campaign-input"
            type="time"
            required
            value={form.scheduleTime}
            onChange={(e) => onChange("scheduleTime", e.target.value)}
            disabled={busy}
          />
        </label>
        <label className="campaign-label">
          Timezone
          <select
            className="campaign-input lm-select"
            value={form.timezone}
            onChange={(e) => onChange("timezone", e.target.value)}
            disabled={busy}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </label>
      </div>

      {form.scheduleDate && form.scheduleTime && (
        <p className="campaign-hint">
          Campaign auto-starts on{" "}
          <strong>{new Date(`${form.scheduleDate}T${form.scheduleTime}`).toLocaleString()}</strong>
        </p>
      )}
    </div>
  );
}

// ─── Step 2: AI Config / Script ──────────────────────────────────────────────
function Step2({ configs, loadingConfigs, form, onChange, busy }) {
  const [expandedId, setExpandedId] = useState(null);

  return (
    <div className="wizard-step-content">
      <div className="wizard-step-title">Select AI Configuration</div>
      <div className="wizard-step-subtitle">Choose the AI agent configuration that will handle these outbound calls.</div>

      {loadingConfigs ? (
        <div className="campaign-hint">Loading configurations…</div>
      ) : !configs.length ? (
        <div className="wizard-empty-scripts">
          <div>No active AI configurations found.</div>
          <div className="campaign-hint">Create one in <strong>Settings → AI Runtime Config</strong> first.</div>
        </div>
      ) : (
        <div className="wizard-script-list">
          {configs.map((c) => {
            const selected = form.aiConfigId === c.id;
            const expanded = expandedId === c.id;
            const scriptPreview = c.outbound_script || c.inbound_script || "";
            return (
              <div
                key={c.id}
                className={`wizard-script-card${selected ? " selected" : ""}`}
                onClick={() => onChange("aiConfigId", c.id)}
              >
                <input
                  type="radio"
                  name="aiConfig"
                  value={c.id}
                  checked={selected}
                  onChange={() => onChange("aiConfigId", c.id)}
                  disabled={busy}
                />
                <div className="wizard-script-info" style={{ flex: 1 }}>
                  <div className="wizard-script-name">{c.name}</div>
                  <div className="wizard-script-meta">
                    {c.agent_name && <span className="wizard-script-tag">Agent: {c.agent_name}</span>}
                    {c.default_language && <span className="wizard-script-tag">{c.default_language}</span>}
                    {c.outbound_script && <span className="wizard-script-tag green">Outbound Script</span>}
                    {!c.outbound_script && c.inbound_script && <span className="wizard-script-tag">Inbound Script</span>}
                  </div>
                  {scriptPreview && (
                    <>
                      <div className="wizard-script-preview" style={{ marginTop: 4 }}>
                        {expanded ? scriptPreview : scriptPreview.slice(0, 160) + (scriptPreview.length > 160 ? "…" : "")}
                      </div>
                      {scriptPreview.length > 160 && (
                        <button
                          type="button"
                          className="wizard-script-expand-btn"
                          onClick={(e) => { e.stopPropagation(); setExpandedId(expanded ? null : c.id); }}
                        >
                          {expanded ? "Show less" : "Show full script"}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Step 3: Audience ─────────────────────────────────────────────────────────
function Step3({ groups, form, onChange, busy }) {
  function toggleGroup(gid) {
    onChange(
      "groupIds",
      form.groupIds.includes(gid)
        ? form.groupIds.filter((id) => id !== gid)
        : [...form.groupIds, gid]
    );
  }

  function updateTestNumber(i, val) {
    const arr = [...form.testNumbers];
    arr[i] = val;
    onChange("testNumbers", arr);
  }

  const totalContacts = form.groupIds.reduce((sum, gid) => {
    const g = groups.find((gr) => gr.group_id === gid);
    return sum + (g?.member_count || 0);
  }, 0);

  return (
    <div className="wizard-step-content">
      <div className="wizard-step-title">Select Target Audience</div>
      <div className="wizard-step-subtitle">Choose which contact groups to call and add test numbers.</div>

      <div className="campaign-label-text">Target Contact Groups <span className="required-star">*</span></div>
      {!groups.length ? (
        <p className="campaign-hint">No groups yet. Create them in Contacts / Leads first.</p>
      ) : (
        <div className="campaign-group-checklist">
          {groups.map((g) => (
            <label
              key={g.group_id}
              className={`campaign-group-check-item${form.groupIds.includes(g.group_id) ? " selected" : ""}`}
            >
              <input
                type="checkbox"
                checked={form.groupIds.includes(g.group_id)}
                onChange={() => toggleGroup(g.group_id)}
                disabled={busy}
              />
              <span className="campaign-group-check-name">{g.name}</span>
              <span className="muted-text">{g.member_count || 0} contact{g.member_count !== 1 ? "s" : ""}</span>
            </label>
          ))}
        </div>
      )}
      {form.groupIds.length > 0 && (
        <div className="campaign-preview">
          <strong>{form.groupIds.length} group{form.groupIds.length !== 1 ? "s" : ""}</strong>
          {" — "}
          <strong>{totalContacts}</strong> contact{totalContacts !== 1 ? "s" : ""} will be called.
          {totalContacts === 0 && (
            <div className="campaign-hint" style={{ marginTop: 6 }}>
              Add contacts to the selected group before scheduling this campaign.
            </div>
          )}
        </div>
      )}

      <div className="campaign-label-text" style={{ marginTop: 16 }}>
        Test Numbers
        <span className="campaign-hint" style={{ marginLeft: 8 }}>(up to 5 — optional test run before the campaign)</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {form.testNumbers.map((num, i) => (
          <div key={i} style={{ display: "flex", gap: 6 }}>
            <input
              className="campaign-input"
              type="text"
              placeholder="+971501234567"
              value={num}
              onChange={(e) => updateTestNumber(i, e.target.value)}
              disabled={busy}
              style={{ flex: 1 }}
            />
            {form.testNumbers.length > 1 && (
              <Button variant="danger" className="compact-btn" disabled={busy}
                onClick={() => onChange("testNumbers", form.testNumbers.filter((_, j) => j !== i))}>
                ×
              </Button>
            )}
          </div>
        ))}
        {form.testNumbers.length < 5 && (
          <Button className="compact-btn" disabled={busy}
            onClick={() => onChange("testNumbers", [...form.testNumbers, ""])}>
            + Add Number
          </Button>
        )}
      </div>

      <label className="campaign-label" style={{ marginTop: 16 }}>
        Call Pacing
        <div className="campaign-pacing">
          <input
            type="range" min="15" max="300" step="5"
            value={form.pacing}
            onChange={(e) => onChange("pacing", e.target.value)}
            disabled={busy}
          />
          <span className="campaign-pacing-val">{form.pacing}s</span>
        </div>
        <span className="campaign-hint">Seconds between each call</span>
      </label>
    </div>
  );
}

// ─── Preview panel ────────────────────────────────────────────────────────────
function PreviewPanel({ form, groups, configs }) {
  const config = configs.find((c) => c.id === form.aiConfigId);
  const selectedGroups = groups.filter((g) => form.groupIds.includes(g.group_id));
  const totalContacts = selectedGroups.reduce((sum, g) => sum + (g.member_count || 0), 0);

  return (
    <div className="wizard-preview-panel">
      <div className="wizard-preview-title">Campaign Preview</div>
      <div className="wizard-preview-row">
        <span className="wizard-preview-key">Name</span>
        <span className="wizard-preview-val">{form.name || <em className="muted-text">—</em>}</span>
      </div>
      <div className="wizard-preview-row">
        <span className="wizard-preview-key">Schedule</span>
        <span className="wizard-preview-val">
          {form.scheduleDate && form.scheduleTime
            ? new Date(`${form.scheduleDate}T${form.scheduleTime}`).toLocaleString()
            : <em className="muted-text">—</em>}
        </span>
      </div>
      <div className="wizard-preview-row">
        <span className="wizard-preview-key">AI Config</span>
        <span className="wizard-preview-val">{config?.name || <em className="muted-text">Not selected</em>}</span>
      </div>
      {config?.agent_name && (
        <div className="wizard-preview-row">
          <span className="wizard-preview-key">Agent</span>
          <span className="wizard-preview-val">{config.agent_name}</span>
        </div>
      )}
      <div className="wizard-preview-row">
        <span className="wizard-preview-key">Groups</span>
        <span className="wizard-preview-val">
          {selectedGroups.length ? selectedGroups.map((g) => g.name).join(", ") : <em className="muted-text">None</em>}
        </span>
      </div>
      <div className="wizard-preview-row">
        <span className="wizard-preview-key">Total contacts</span>
        <span className="wizard-preview-val">{totalContacts}</span>
      </div>
      <div className="wizard-preview-row">
        <span className="wizard-preview-key">Pacing</span>
        <span className="wizard-preview-val">{form.pacing}s between calls</span>
      </div>
    </div>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────────────────
export default function CampaignCreate({ groups, onCreated, onBack, showToast }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);

  const [form, setForm] = useState({
    name: "",
    description: "",
    scheduleDate: "",
    scheduleTime: "",
    timezone: "Asia/Dubai",
    aiConfigId: null,
    groupIds: [],
    testNumbers: [""],
    pacing: 45,
  });

  function onChange(key, val) {
    setForm((prev) => ({ ...prev, [key]: val }));
  }

  function getSelectedContactCount() {
    return groups
      .filter((group) => form.groupIds.includes(group.group_id))
      .reduce((sum, group) => sum + (group.member_count || 0), 0);
  }

  useEffect(() => {
    setLoadingConfigs(true);
    getAiConfigs({ limit: 100 })
      .then((data) => setConfigs((data.items || []).filter((c) => c.status === "ACTIVE")))
      .catch(() => setConfigs([]))
      .finally(() => setLoadingConfigs(false));
  }, []);

  function validateStep() {
    if (step === 1) {
      if (!form.name.trim()) { showToast("Campaign name is required.", true); return false; }
      if (!form.scheduleDate || !form.scheduleTime) { showToast("Please pick a date and time.", true); return false; }
      const dt = new Date(`${form.scheduleDate}T${form.scheduleTime}`);
      if (dt <= new Date()) { showToast("Scheduled time must be in the future.", true); return false; }
    }
    if (step === 2) {
      if (!form.aiConfigId) { showToast("Please select an AI configuration.", true); return false; }
    }
    if (step === 3) {
      if (!form.groupIds.length) { showToast("Select at least one contact group.", true); return false; }
      if (getSelectedContactCount() < 1) {
        showToast("Selected groups have 0 contacts. Add contacts to the group first.", true);
        return false;
      }
    }
    return true;
  }

  async function handleSubmit() {
    if (!validateStep() || busy) return;
    const scheduledAt = new Date(`${form.scheduleDate}T${form.scheduleTime}`);
    const validTestNumbers = form.testNumbers.map((n) => n.trim()).filter(Boolean);
    setBusy(true);
    try {
      const result = await createCampaign({
        name: form.name.trim(),
        offer_message: form.description.trim() || form.name.trim(),
        ai_config_id: form.aiConfigId,
        target_type: "group_ids",
        group_ids: form.groupIds,
        test_numbers: validTestNumbers,
        scheduled_at: scheduledAt.toISOString(),
        pacing_seconds: Number(form.pacing),
      });
      showToast(
        `Campaign scheduled for ${scheduledAt.toLocaleString()} — ${result?.targets_count || 0} targets.`,
        false,
        5000
      );
      onCreated();
    } catch (err) {
      showToast(normalizeApiError(err).message, true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="subsection-row">
        <Button onClick={onBack} disabled={busy}>&larr; Back</Button>
        <div className="section-title section-title-no-margin">New Campaign</div>
      </div>

      <StepBar step={step} />

      <div className="wizard-layout">
        {/* Main content */}
        <div className="wizard-main">
          {step === 1 && <Step1 form={form} onChange={onChange} busy={busy} />}
          {step === 2 && <Step2 configs={configs} loadingConfigs={loadingConfigs} form={form} onChange={onChange} busy={busy} />}
          {step === 3 && <Step3 groups={groups} form={form} onChange={onChange} busy={busy} />}

          <div className="wizard-nav">
            {step > 1 && (
              <Button onClick={() => setStep((s) => s - 1)} disabled={busy}>← Back</Button>
            )}
            <div style={{ flex: 1 }} />
            {step < 3 ? (
              <Button variant="primary" onClick={() => { if (validateStep()) setStep((s) => s + 1); }} disabled={busy}>
                Next →
              </Button>
            ) : (
              <Button variant="gold" onClick={handleSubmit} disabled={busy}>
                {busy ? "Scheduling…" : "Schedule Campaign"}
              </Button>
            )}
          </div>
        </div>

        {/* Preview sidebar */}
        <PreviewPanel form={form} groups={groups} configs={configs} />
      </div>
    </div>
  );
}
